import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import { EyeIcon, InboxStackIcon, SunIcon, ArrowLongDownIcon, CurrencyBangladeshiIcon, UsersIcon, CurrencyDollarIcon, DocumentCheckIcon, MoonIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

function Dashboard() {
  const [isMobile, setIsMobile] = useState(false);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    axios.get('/dashboard/summary').then(res => setSummary(res.data));
    axios.get('/dashboard/latest-transactions').then(res => setTransactions(res.data));
    axios.get('/dashboard/top-products').then(res => setProducts(res.data));
    axios.get('/dashboard/low-stock').then(res => setLowStock(res.data));
    axios.get('/dashboard/top-customers').then(res => setCustomers(res.data));
  }, []);
  const statCards = [
    {
      title: 'Total Sales',
      icon: <CurrencyDollarIcon className="text-green-700 text-2xl" />,
      value: summary?.total_sales_amount || 0
    },
    {
      title: 'Total Purchases',
      icon: <MoonIcon className=" text-amber-600 text-2xl" />,
      value: summary?.total_purchase_amount || 0
    },
    {
      title: 'Total Expenses',
      icon: <InboxStackIcon className=" text-red-900 text-2xl" />,
      value: summary?.total_expense_amount || 0
    },
    {
      title: 'Current Balance',
      icon: <CurrencyBangladeshiIcon className="text-blue-500 text-2xl" />,
      value: summary?.current_balance || 0
    },
    {
      title: 'Total Customers',
      icon: <UsersIcon className="text-2xl text-purple-600" />,
      value: summary?.total_customers || 0
    },
    {
      title: 'Total Suppliers',
      icon: <SunIcon className="text-yellow-400 text-2xl" />,
      value: summary?.total_suppliers || 0
    },
    {
      title: 'Total Sales Due',
      icon: <ArrowLongDownIcon className="text-red-500 text-2xl" />,
      value: summary?.total_sales_due || 0
    },
    {
      title: 'Total Purchases Due',
      icon: <EyeIcon className=" text-sky-500 text-2xl" />,
      value: summary?.total_purchase_due || 0
    },
  ];

  return (
    <div className="flex">
      {/*  */}
      <div className="p-2 w-full">
        <h2 className="text-2xl font-bold mb-4">Dashboard</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-4 max-sm:mb-2">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="rounded-2xl bg-white shadow-md p-4 flex items-center gap-4 border border-gray-200"
            >
              <div className="bg-gray-100 w-16 p-3 rounded-full">
                {card.icon}
              </div>
              <div>
                <p className="text-sm font-bold text-blue-900">{card.title}</p>
                <p className="text-xl font-bold">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4 mb-4 max-sm:mb-2">
          <div className=' overflow-auto bg-white p-2 rounded-md shadow-md'>
            <h2 className="text-lg font-bold mb-1 text-blue-900">Latest Transactions</h2>
            <table className="w-full border text-sm">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="border px-2 py-1 text-left">{isMobile ? "Bank" : "Bank Name"}</th>
                  <th className="border px-2 py-1 text-left">{isMobile ? "Des" : "Description"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Type" : "Type"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Amt" : "Amount"}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? transactions.map((bank, i) => (
                  <tr className="odd:bg-white even:bg-gray-100" key={i}>
                    <td className="border px-2 py-1">{bank.bank_name}</td>
                    <td className="border px-2 py-1">{bank.description}</td>
                    <td className={`border px-2 py-1 ${bank.type === 'withdraw' ? "text-red-600" : bank.type === 'deposit' ? 'text-green-600' : 'text-blue-600'}`}>{bank.type}</td>
                    <td className={`border px-2 py-1 text-right ${bank.type === 'withdraw' ? "text-red-600" : bank.type === 'deposit' ? 'text-green-600' : 'text-blue-600'}`}>{Number(bank.amount).toFixed(2)}</td>
                  </tr>
                )) : <tr>
                  <td colSpan={4} className='border px-2 py-1 text-center'>No Transaction Found</td>
                </tr>}
              </tbody>
            </table>
          </div>
          <div className=' overflow-auto bg-white p-2 rounded-md shadow-md'>
            <h2 className="text-lg font-bold mb-1 text-blue-900">Top selling Product</h2>
            <table className="w-full border text-sm">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="border px-2 py-1 text-left">#</th>
                  <th className="border px-2 py-1 text-left">{isMobile ? "Name" : "Name"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Qty" : "Quantity"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Amt" : "Total Amount"}</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? products.map((p, i) => (
                  <tr className="odd:bg-white even:bg-gray-100" key={i}>
                    <td className="border px-2 py-1">{i + 1}</td>
                    <td className="border px-2 py-1">{p.name}</td>
                    <td className="border px-2 py-1 text-right font-bold">{p.total_quantity}</td>
                    <td className="border px-2 py-1 text-right font-bold">{Number(p.total_sales).toFixed(2)} BDT</td>
                  </tr>
                )) : <tr>
                  <td colSpan={4} className='border px-2 py-1 text-center'>No Products Found</td>
                </tr>}
              </tbody>
            </table>
          </div>
          <div className=' overflow-auto bg-white p-2 rounded-md shadow-md'>
            <h2 className="text-lg font-bold mb-1 text-blue-900">Low Stock Products</h2>
            <table className="w-full border text-sm">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="border px-2 py-1 text-left">#</th>
                  <th className="border px-2 py-1 text-left">{isMobile ? "Name" : "Name"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Min Qty" : "Min Quantity"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "C. Qty" : "Current Quantity"}</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.length > 0 ? lowStock.map((p, i) => (
                  <tr className="odd:bg-white even:bg-gray-100" key={i}>
                    <td className="border px-2 py-1">{i + 1}</td>
                    <td className="border px-2 py-1">{p.name}</td>
                    <td className="border px-2 py-1 text-right font-bold">{p.min_qty}</td>
                    <td className="border px-2 py-1 text-right font-bold">{p.quantity}</td>
                  </tr>
                )) : <tr>
                  <td colSpan={4} className='border px-2 py-1 text-center'>No Products Found</td>
                </tr>}
              </tbody>
            </table>
          </div>
          <div className=' overflow-auto bg-white p-2 rounded-md shadow-md'>
            <h2 className="text-lg font-bold mb-1 text-blue-900">Top Customers</h2>
            <table className="w-full border text-sm">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="border px-2 py-1 text-left">#</th>
                  <th className="border px-2 py-1 text-left">{isMobile ? "Name" : "Name"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Ads" : "Address"}</th>
                  <th className="border px-2 py-1 text-right">{isMobile ? "Sales" : "Sales"}</th>
                </tr>
              </thead>
              <tbody>
                {customers.length > 0 ? customers.map((p, i) => (
                  <tr className="odd:bg-white even:bg-gray-100" key={i}>
                    <td className="border px-2 py-1">{i + 1}</td>
                    <td className="border px-2 py-1">{p.name}</td>
                    <td className="border px-2 py-1">{p.address}</td>
                    <td className="border px-2 py-1 text-right font-bold">{Number(p.total_sales).toFixed(2)} BDT</td>
                  </tr>
                )) : <tr>
                  <td colSpan={4} className='border px-2 py-1 text-center'>No Customer Found</td>
                </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Dashboard;